/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LightbulbSVG from 'src/assets/svg/lightbulb.svg';
import { useCreateWizard, useWizard } from 'src/queries/Wizard/wizard';
import CircledIcon from 'src/components/CircledIcon/CircledIcon';
import { appUtils, Base } from 'src/components/index';
import SummaryAICardV3 from 'src/containers/UserProfile/Wizard/components/SummaryAICardV3';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import STYLE from 'src/constants/style';

const { WIZARD_SUMMARIES_RANGE } = COMMON_CONSTANTS;

const MonthlySummary = ({ user = { id: null } }) => {
  const range = WIZARD_SUMMARIES_RANGE.LAST_MONTH;
  const { createWizardSummary, isLoading: isLoadingCreateWizard } = useCreateWizard();
  const {
    data: wizards,
    isFetching: isFetchingWizard,
    isError: isErrorWizard,
    isPlaceholderData: isPlaceholderDataWizard
  } = useWizard({
    filters: { account: user.id },
    options: {
      sort: {
        field: 'end',
        order: 'desc'
      },
      size: range.value,
      stringify: true
    }
  });
  const { showMonthlySummaryOnProfile } = useFlags();

  const isFetching = isFetchingWizard;
  const isError = isErrorWizard;
  const isPlaceholderData = isPlaceholderDataWizard;
  const isReady = !isPlaceholderData && !isFetching && !isError;

  const unixToday = commonDateUtils.dateToUnix(new Date());
  const month = commonDateUtils.unixToMonth(unixToday);

  useEffect(() => {
    if (!isReady) return;

    const months = wizards.map((i) => commonDateUtils.unixToMonth(i.end));
    const isThereASummaryForMonth = months.includes(month);
    if (!isThereASummaryForMonth && showMonthlySummaryOnProfile) createWizardSummary(user.id);
  }, [isReady, month, showMonthlySummaryOnProfile, user.id, wizards.length]);

  if (!isReady) return null;

  const isSelf = user.id === appUtils.getLoggedUserId();

  return (
    <Base
      classes={`${STYLE.CONTAINER_WHITE_MARGINLESS} !p-4`}
      loading={isFetching || isLoadingCreateWizard}
      relative
    >
      <CircledIcon svg={<LightbulbSVG className='text-purple' />} />
      <p className='text-xl text-black font-bold mb-0 mr-8'>{`Performance Summary - ${month}`}</p>
      <p className='text-md font-normal my-2 leading-5 text-bluish-gray'>
        An auto-generated summary based on team feedback.
      </p>
      <div className='border-b border-mid-gray my-3 transform -translate-x-[16px] w-[calc(100%+32px)]' />
      {wizards.length ? (
        wizards.map((s, index) => (
          <div key={index}>
            <SummaryAICardV3 strings={s.strings} endDate={s.end} />
          </div>
        ))
      ) : (
        <div>
          <h3 className='text-lg font-bold text-black'>Nothing here yet!</h3>
          <div className='text-md text-bluish-gray'>
            Once
            {isSelf
              ? ' you have received '
              : ` ${user.name || 'this person'} receives `}
            enough feedback, we’ll create a summary and provide some performance
            recommendations.
          </div>
        </div>
      )}
    </Base>
  );
};

export default MonthlySummary;
